import React, { useContext, useEffect, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import * as AiIcons from 'react-icons/ai';
import * as HiIcons from 'react-icons/hi';
import {
  add,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isEqual,
  isToday,
  parse,
  startOfToday,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import ReactModal from 'react-modal';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';

import { IReserva } from '../../types/Reserva';
import api from '../../services/api';
import Context, { IContext } from '../../context/Context';
import CustomAlert from '../CustomAlert';
import { ITipEsp } from '../../types/TipEsp';

interface IFiltrosPlanilha {
  datini: string;
  datfin: string;
  codtipesp: string;
}

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ')
}

// Datas das reservas já chegam da API em dd/MM/yyyy, então comparamos pela mesma string
function chaveDia(dia: Date) {
  return format(dia, 'dd/MM/yyyy');
}

function iniciais(nome?: string) {
  return (nome || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(parte => parte[0]).join('').toUpperCase();
}

// Somente no mobile: rola suavemente até a seção informada
function rolarNoMobile(idSecao: string) {
  if (!window.matchMedia('(max-width: 767px)').matches) return;

  const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  setTimeout(() => {
    document.getElementById(idSecao)?.scrollIntoView({ behavior, block: 'start' });
  }, 100);
}

export default function Agenda() {
  const navigate = useNavigate();
  const { caminho } = useParams();
  const { setIsLoadingOverlay, }: IContext = useContext(Context);

  const hoje = startOfToday();

  const [diaSelecionado, setDiaSelecionado] = useState(hoje);
  const [mesAtual, setMesAtual] = useState(format(hoje, 'MMM-yyyy'));
  const [reservas, setReservas] = useState<IReserva[]>([]);
  const [reservaEditando, setReservaEditando] = useState<IReserva | undefined>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [filtrosModal, setFiltrosModal] = useState<IFiltrosPlanilha>({ datini: '', datfin: '', codtipesp: '' });
  const [tiposDeEspaco, setTiposDeEspaco] = useState<{ value: number; label: string }[]>([]);

  const primeiroDiaDoMesAtual = parse(mesAtual, 'MMM-yyyy', new Date());

  const dias = eachDayOfInterval({
    start: primeiroDiaDoMesAtual,
    end: endOfMonth(primeiroDiaDoMesAtual),
  })

  function mesAnterior() {
    const primeiroDiaDoMesAnterior = add(primeiroDiaDoMesAtual, { months: -1 });
    setMesAtual(format(primeiroDiaDoMesAnterior, 'MMM-yyyy'));
  }

  function proximoMes() {
    const primeiroDiaDoProximoMes = add(primeiroDiaDoMesAtual, { months: 1 })
    setMesAtual(format(primeiroDiaDoProximoMes, 'MMM-yyyy'))
  }

  function irParaHoje() {
    setMesAtual(format(hoje, 'MMM-yyyy'));
    if (!reservaEditando) setDiaSelecionado(hoje);
  }

  const reservasPorDia = reservas.reduce<Record<string, IReserva[]>>((acumulador, reserva) => {
    (acumulador[reserva.datres] = acumulador[reserva.datres] || []).push(reserva);
    return acumulador;
  }, {});

  const reservasDoDiaSelecionado = [...(reservasPorDia[chaveDia(diaSelecionado)] || [])]
    .sort((a, b) => a.espaco.esp.localeCompare(b.espaco.esp));

  // Na alteração de data, o espaço não pode ir para um dia em que já possui reserva PAGO
  function espacoOcupadoNoDia(dia: Date) {
    if (!reservaEditando) return false;

    return (reservasPorDia[chaveDia(dia)] || [])
      .some(reserva => reserva.espaco.cod === reservaEditando.espaco.cod);
  }

  function ehDiaDaReservaEditando(dia: Date) {
    return !!reservaEditando && reservaEditando.datres === chaveDia(dia);
  }

  async function getTiposDeEspaco() {
    try {
      const response = await api.get('/tiposDeEspaco');
      if (response.status === 200) {
        const options = response.data.map((tipo: ITipEsp) => ({ value: tipo.cod, label: tipo.tipesp }));
        setTiposDeEspaco(options);
      }
    } catch (error: any) {
      toast.error('Erro ao buscar Tipos de Espaço');
    }
  }

  async function getReservas() {
    try {
      setIsLoadingOverlay(true);
      const response = await api.get('/reservas', {
        params: {
          sta: 'PAGO'
        }
      });

      if (response.status === 200) {
        setReservas(response.data);
      }
    } catch (error: any) {
      if (error.response?.data?.erro) return toast.error(error.response.data.erro);
      toast.error('Erro ao buscar reservas');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function putReserva(reserva: IReserva, novaData: Date) {
    try {
      setIsLoadingOverlay(true);
      const payload = {
        datres: novaData.toISOString().substring(0, 10),
      }

      const response = await api.patch(`/reservas/${reserva.cod}`, payload);

      if (response.status === 200) {
        toast.success('Reserva editada com sucesso.');
        getReservas();
      }
    } catch (error: any) {
      if (error.response?.data?.erro?.includes('já reservado para data')) return toast.error(`${reserva.espaco.esp} já foi reservado para essa data.`);
      if (error.response?.data?.erro) return toast.error(error.response.data.erro);
      toast.error('Erro ao editar reserva.');
    } finally {
      setReservaEditando(undefined);
      setIsLoadingOverlay(false);
    }
  }

  async function deletarReserva(id: number) {
    try {
      setIsLoadingOverlay(true);
      const response = await api.delete(`/reservas/${id}`);
      if (response.status === 204) {
        toast.success('Reserva cancelada com sucesso.');
        getReservas();
      }
    } catch (error: any) {
      if (error.response?.data?.erro) return toast.error(error.response.data.erro);
      toast.error('Erro ao excluir reserva.');
    } finally {
      setIsLoadingOverlay(false);
    }
  }

  async function getPlanilha() {
    try {
      setIsLoadingOverlay(true);
      const response = await api.get('/reservas/exportar', {
        params: {
          sta: 'PAGO',
          ordem: 'desc',
          ...(filtrosModal.datini && { datini: filtrosModal.datini }),
          ...(filtrosModal.datfin && { datfin: filtrosModal.datfin }),
          ...(filtrosModal.codtipesp && { codtipesp: +filtrosModal.codtipesp }),
        },
        responseType: 'blob'
      });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Reservas.xlsx');
        document.body.appendChild(link);
        link.click();
        setModalVisible(false);
      }
    } catch (error: any) {
      if (error.response?.status === 404) return toast.warning('Nenhum registro encontrado.');
      toast.error('Erro ao gerar planilha.');
    } finally {
      setIsLoadingOverlay(false);
    }
  }

  async function handleDiaButtonClick(dia: Date) {
    if (reservaEditando) {
      if (ehDiaDaReservaEditando(dia)) return setReservaEditando(undefined);

      CustomAlert({
        title: `Alterando Data de Reserva ${reservaEditando.espaco.esp}`,
        message: `Deseja alterar a data de reserva para ${format(dia, 'dd/MM/yyyy', { locale: ptBR })}?`,
        onConfirm: async () => {
          await putReserva(reservaEditando, dia)
          setDiaSelecionado(dia);
        },
        onCancel: () => setReservaEditando(undefined)
      })

      return;
    }

    setDiaSelecionado(dia);
  }

  function iniciarAlteracaoDeData(reserva: IReserva) {
    setReservaEditando(reserva);
    rolarNoMobile('calendarioAgenda');
  }

  useEffect(() => {
    getReservas();
    getTiposDeEspaco();
  }, []);

  const totalReservasNoMes = dias.reduce((total, dia) => total + (reservasPorDia[chaveDia(dia)]?.length || 0), 0);

  return (
    <div className='w-full max-w-5xl mx-auto px-4 pb-10'>
      <ModalExportarPlanilha
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        filtros={filtrosModal}
        setFiltros={setFiltrosModal}
        tiposDeEspaco={tiposDeEspaco}
        onGerar={getPlanilha}
      />

      <header className='mb-6 mt-4 flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>Agenda</h1>
          <p className='mt-1 text-zinc-500'>Reservas confirmadas de todos os espaços.</p>
        </div>
        <button
          type='button'
          onClick={() => setModalVisible(true)}
          className='flex h-10 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        >
          <AiIcons.AiOutlineFileExcel className='h-5 w-5' aria-hidden='true' />
          Exportar planilha
        </button>
      </header>

      <div className='grid gap-6 md:grid-cols-[1fr_360px] md:items-start'>
        <section id='calendarioAgenda' className='scroll-mt-20 space-y-3'>
          {reservaEditando && (
            <div
              role='status'
              aria-live='polite'
              className='flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 animate-slide-up motion-reduce:animate-none'
            >
              <AiIcons.AiOutlineCalendar className='mt-0.5 h-5 w-5 flex-none text-amber-600' aria-hidden='true' />
              <p className='flex-1'>
                Alterando a data de <span className='font-semibold'>{reservaEditando.espaco.esp}</span> ({reservaEditando.datres}).
                {' '}Selecione a nova data no calendário.
              </p>
              <button
                type='button'
                onClick={() => setReservaEditando(undefined)}
                className='flex-none rounded-lg px-2 py-1 font-semibold text-amber-900 underline underline-offset-4 hover:text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600'
              >
                Cancelar
              </button>
            </div>
          )}

          <div className='rounded-2xl border border-zinc-200 bg-white p-3 shadow-card sm:p-5'>
            <div className='mb-4 flex items-center gap-1'>
              <div className='flex-auto'>
                <h2 className='text-lg font-semibold capitalize text-zinc-900'>
                  {format(primeiroDiaDoMesAtual, 'MMMM yyyy', { locale: ptBR })}
                </h2>
                <p className='text-xs text-zinc-500'>
                  {totalReservasNoMes === 0 ? 'Nenhuma reserva no mês' : `${totalReservasNoMes} ${totalReservasNoMes === 1 ? 'reserva' : 'reservas'} no mês`}
                </p>
              </div>
              <button
                type='button'
                onClick={irParaHoje}
                className='mr-1 h-9 rounded-full border border-zinc-200 px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
              >
                Hoje
              </button>
              <button
                type='button'
                onClick={mesAnterior}
                aria-label='Mês anterior'
                className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
              >
                <AiIcons.AiOutlineLeft className='w-5 h-5' aria-hidden='true' />
              </button>
              <button
                type='button'
                onClick={proximoMes}
                aria-label='Próximo mês'
                className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
              >
                <AiIcons.AiOutlineRight className='w-5 h-5' aria-hidden='true' />
              </button>
            </div>

            <div className='grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-zinc-400 sm:text-xs'>
              {diasDaSemana.map(diaDaSemana => (
                <div key={diaDaSemana} className='py-1'>{diaDaSemana}</div>
              ))}
            </div>

            <div className='mt-1 grid grid-cols-7 gap-1'>
              {dias.map((dia, index) => {
                const quantidadeReservas = reservasPorDia[chaveDia(dia)]?.length || 0;
                const diaDaReservaEditando = ehDiaDaReservaEditando(dia);
                const ocupado = espacoOcupadoNoDia(dia) && !diaDaReservaEditando;
                const disponivelParaAlteracao = !!reservaEditando && !ocupado && !diaDaReservaEditando;
                const selecionado = !reservaEditando && isEqual(dia, diaSelecionado);

                return (
                  <div
                    key={dia.toString()}
                    className={classNames(index === 0 && colStartClasses[getDay(dia)])}
                  >
                    <button
                      type='button'
                      onClick={() => handleDiaButtonClick(dia)}
                      disabled={ocupado}
                      aria-pressed={selecionado || diaDaReservaEditando}
                      aria-label={`${format(dia, "d 'de' MMMM", { locale: ptBR })}, ${quantidadeReservas} ${quantidadeReservas === 1 ? 'reserva' : 'reservas'}${ocupado ? ', espaço já reservado' : ''}`}
                      className={classNames(
                        'relative flex h-14 w-full flex-col items-center justify-center rounded-xl text-sm transition-colors md:h-16',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-1',
                        selecionado && 'border border-zinc-900 bg-zinc-900 font-semibold text-white shadow-card',
                        !reservaEditando && !selecionado && 'border border-zinc-200 bg-white font-semibold text-zinc-900 hover:border-zinc-900 hover:bg-zinc-50',
                        diaDaReservaEditando && 'border border-amber-400 bg-amber-50 font-semibold text-amber-900',
                        disponivelParaAlteracao && 'border border-emerald-200 bg-emerald-50/50 font-semibold text-zinc-900 hover:border-emerald-600 hover:bg-emerald-50',
                        ocupado && 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
                        isToday(dia) && !selecionado && 'ring-1 ring-inset ring-zinc-900'
                      )}
                    >
                      <time dateTime={format(dia, 'yyyy-MM-dd')}>
                        {format(dia, 'dd')}
                      </time>
                      {ocupado ? (
                        <span className='mt-0.5 text-[9px] font-medium leading-none md:text-[10px]'>Ocupado</span>
                      ) : quantidadeReservas > 0 && (
                        <span className={`mt-1 flex items-center gap-1 text-[10px] font-medium leading-none md:text-xs ${selecionado ? 'text-zinc-300' : 'text-zinc-500'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${selecionado ? 'bg-white' : 'bg-zinc-900'}`} aria-hidden='true' />
                          {quantidadeReservas}
                          <span className='hidden md:inline'>{quantidadeReservas === 1 ? 'reserva' : 'reservas'}</span>
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            <ul className='mt-5 flex flex-wrap gap-x-4 gap-y-2 border-t border-zinc-100 pt-4 text-xs text-zinc-500'>
              {reservaEditando ? (
                <>
                  <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded border border-emerald-300 bg-emerald-50' />Disponível para o espaço</li>
                  <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded border border-amber-400 bg-amber-50' />Data atual</li>
                  <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded bg-zinc-100' />Ocupado</li>
                </>
              ) : (
                <>
                  <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded bg-zinc-900' />Selecionado</li>
                  <li className='flex items-center gap-1.5'><span className='h-1.5 w-1.5 rounded-full bg-zinc-900' />Com reservas</li>
                </>
              )}
              <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded ring-1 ring-inset ring-zinc-900' />Hoje</li>
            </ul>
          </div>
        </section>

        <aside className='md:sticky md:top-20'>
          <div className='rounded-2xl border border-zinc-200 bg-white shadow-card'>
            <div className='border-b border-zinc-100 px-5 py-4'>
              <p className='text-xs font-medium uppercase tracking-wide text-zinc-400'>Reservas do dia</p>
              <h2 className='mt-0.5 text-lg font-semibold capitalize text-zinc-900'>
                <time dateTime={format(diaSelecionado, 'yyyy-MM-dd')}>
                  {format(diaSelecionado, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                </time>
              </h2>
            </div>

            <ol aria-live='polite' className='divide-y divide-zinc-100'>
              {reservasDoDiaSelecionado.length > 0 ? (
                reservasDoDiaSelecionado.map((reserva) => (
                  <Reserva
                    reserva={reserva}
                    key={reserva.cod}
                    editando={reservaEditando?.cod === reserva.cod}
                    iniciarAlteracaoDeData={iniciarAlteracaoDeData}
                    deletarReserva={deletarReserva}
                    navigate={navigate}
                    caminho={caminho!}
                  />
                ))
              ) : (
                <li className='flex flex-col items-center gap-2 px-6 py-10 text-center text-sm text-zinc-400'>
                  <AiIcons.AiOutlineCalendar className='h-8 w-8' aria-hidden='true' />
                  Sem reservas para o dia selecionado.
                </li>
              )}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  )
}

function Reserva(
  {
    reserva,
    editando,
    iniciarAlteracaoDeData,
    deletarReserva,
    navigate,
    caminho
  }:
    {
      reserva: IReserva,
      editando: boolean,
      iniciarAlteracaoDeData: (reserva: IReserva) => void,
      deletarReserva: (id: number) => void,
      navigate: NavigateFunction,
      caminho: string;
    }) {
  return (
    <li className={classNames(
      'flex items-start gap-3 px-5 py-4 animate-fade-in motion-reduce:animate-none',
      editando && 'bg-amber-50'
    )}>
      <span className='flex h-10 w-10 flex-none items-center justify-center rounded-full bg-zinc-100 text-sm font-semibold text-zinc-700'>
        {iniciais(reserva.usuario?.usu) || <AiIcons.AiOutlineUser className='h-5 w-5' aria-hidden='true' />}
      </span>
      <div className='min-w-0 flex-1'>
        <p className='font-semibold text-zinc-900'>{reserva.espaco.esp}</p>
        {reserva.tipres?.des && (
          <span className='mt-1 inline-block rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600'>
            {reserva.tipres.des}
          </span>
        )}
        <p className='mt-1 truncate text-sm text-zinc-500'>{reserva.usuario?.usu}</p>
        {reserva.obs && (
          <p className='mt-1 break-words text-sm text-zinc-500'><span className='font-medium text-zinc-700'>Obs:</span> {reserva.obs}</p>
        )}
      </div>
      <Menu as='div' className='relative flex-none'>
        <MenuButton className='flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'>
          <span className='sr-only'>Opções da reserva {reserva.espaco.esp}</span>
          <HiIcons.HiDotsVertical className='h-5 w-5' aria-hidden='true' />
        </MenuButton>

        <MenuItems
          transition
          // anchor renderiza em portal: o animate-fade-in do <li> cria stacking context e o próximo card cobriria o menu
          anchor='bottom end'
          className='z-20 w-52 origin-top-right [--anchor-gap:8px] overflow-hidden rounded-xl border border-zinc-200 bg-white p-1 shadow-card focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
        >
          <MenuItem>
            <button
              type='button'
              onClick={() => navigate(`/${caminho}/reservaDetalhes/${reserva.cod}`)}
              className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 data-[focus]:bg-zinc-100 data-[focus]:text-zinc-900'
            >
              <AiIcons.AiOutlineTeam className='h-4 w-4' aria-hidden='true' />
              Lista de convidados
            </button>
          </MenuItem>
          <MenuItem>
            <button
              type='button'
              onClick={() => iniciarAlteracaoDeData(reserva)}
              className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 data-[focus]:bg-zinc-100 data-[focus]:text-zinc-900'
            >
              <AiIcons.AiOutlineCalendar className='h-4 w-4' aria-hidden='true' />
              Alterar data
            </button>
          </MenuItem>
          <MenuItem>
            <button
              type='button'
              onClick={() => CustomAlert({
                title: 'Cancelamento de Reserva',
                message: 'Deseja prosseguir com o cancelamento da reserva?',
                onConfirm: () => deletarReserva(reserva.cod),
                onCancel: () => { },
              })}
              className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-600 data-[focus]:bg-rose-50'
            >
              <AiIcons.AiOutlineCloseCircle className='h-4 w-4' aria-hidden='true' />
              Cancelar reserva
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
    </li>
  )
}

function ModalExportarPlanilha(
  {
    isOpen,
    onClose,
    filtros,
    setFiltros,
    tiposDeEspaco,
    onGerar
  }:
    {
      isOpen: boolean,
      onClose: () => void,
      filtros: IFiltrosPlanilha,
      setFiltros: React.Dispatch<React.SetStateAction<IFiltrosPlanilha>>,
      tiposDeEspaco: { value: number; label: string }[],
      onGerar: () => void,
    }) {
  const classeCampo = 'h-12 w-full rounded-xl border border-zinc-300 bg-white px-4 text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10';

  return (
    <ReactModal
      isOpen={isOpen}
      appElement={document.getElementById('root') as HTMLElement}
      contentLabel='Exportar planilha de reservas'
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      overlayClassName='fixed inset-0 z-[99] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in motion-reduce:animate-none'
      className='relative flex w-full max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-card outline-none animate-slide-up motion-reduce:animate-none md:max-w-md md:rounded-2xl'
    >
      <div className='flex items-center justify-between border-b border-zinc-100 px-5 py-4'>
        <div>
          <h2 className='text-lg font-semibold text-zinc-900'>Exportar planilha</h2>
          <p className='text-sm text-zinc-500'>Reservas confirmadas, em Excel.</p>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Fechar'
          className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        >
          <AiIcons.AiOutlineClose className='h-5 w-5' aria-hidden='true' />
        </button>
      </div>

      <div className='space-y-4 overflow-y-auto px-5 py-5 pb-[max(20px,env(safe-area-inset-bottom))]'>
        <div className='grid gap-4 sm:grid-cols-2'>
          <div>
            <label htmlFor='datiniPlanilha' className='mb-1 block text-sm font-medium text-zinc-700'>Data inicial</label>
            <input
              id='datiniPlanilha'
              type='date'
              onChange={(e) => setFiltros({ ...filtros, datini: e.target.value })}
              value={filtros.datini}
              className={classeCampo}
            />
          </div>
          <div>
            <label htmlFor='datfinPlanilha' className='mb-1 block text-sm font-medium text-zinc-700'>Data final</label>
            <input
              id='datfinPlanilha'
              type='date'
              min={filtros.datini || undefined}
              onChange={(e) => {
                if (!filtros.datini) return toast.error('Data inicial deve ser informada antes de informar a data final.');
                if (e.target.value < filtros.datini) return;
                setFiltros({ ...filtros, datfin: e.target.value })
              }}
              value={filtros.datfin}
              className={classeCampo}
            />
          </div>
        </div>
        <div>
          <label htmlFor='codtipespPlanilha' className='mb-1 block text-sm font-medium text-zinc-700'>Tipo de espaço</label>
          <select
            id='codtipespPlanilha'
            value={filtros.codtipesp}
            onChange={(e) => setFiltros({ ...filtros, codtipesp: e.target.value })}
            className={classeCampo}
          >
            <option value=''>Qualquer espaço</option>
            {tiposDeEspaco.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <button
          type='button'
          onClick={onGerar}
          className='flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2'
        >
          <AiIcons.AiOutlineDownload aria-hidden='true' />
          Gerar planilha
        </button>
      </div>
    </ReactModal>
  )
}

const diasDaSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

let colStartClasses = [
  '',
  'col-start-2',
  'col-start-3',
  'col-start-4',
  'col-start-5',
  'col-start-6',
  'col-start-7',
]
